import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';

declare global {
  interface Window {
    tap?: (event: string, ...args: unknown[]) => void;
  }
}

type OrderRow = {
  total_cents: number | null;
  seeker_id: string | null;
};

export const CheckoutSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = location.state?.orderId;
  const [order, setOrder] = useState<OrderRow | null>(null);
  const tapFiredRef = useRef(false);

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!orderId) return;

      try {
        const supabase = getSupabaseClient();

        // Get the order for tapfiliate tracking
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('total_cents, seeker_id')
          .eq('id', orderId)
          .single();

        if (orderError) {
          console.error('Failed to fetch order:', orderError);
          return;
        }

        setOrder(orderData as OrderRow);
      } catch (err) {
        console.error('Error fetching order:', err);
      }
    };

    fetchOrderData();
  }, [orderId]);

  // Tapfiliate conversion tracking
  useEffect(() => {
    if (!orderId || !order || tapFiredRef.current) return;

    // Verify all conditions before firing
    if (
      typeof window.tap !== 'function' ||
      order.seeker_id == null ||
      order.total_cents == null
    ) {
      return;
    }

    const uniqueConversionId = orderId as string;
    const conversionAmount = order.total_cents / 100;
    const customerId = order.seeker_id;

    window.tap('conversion', uniqueConversionId, conversionAmount, {
      customer_id: customerId,
    });

    tapFiredRef.current = true;
  }, [orderId, order]);

  return (
    <div className="flex min-h-screen flex-col bg-[#FFFCEF]">
      <header className="bg-[#2C2731] text-[#FFFCEF]">
        <div className="mx-auto flex max-w-sm items-center justify-between px-4 py-4">
          <span className="text-lg font-semibold">Payment Successful</span>
          <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-8 text-center shadow-xl shadow-[#2C2731]/15">
          <div className="mb-6 flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[#2C2731]/30 bg-[#E8DFBD]">
              <svg
                className="h-10 w-10 text-[#2C2731]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          <h2 className="mb-2 text-2xl font-bold text-[#2C2731]">Payment Complete!</h2>
          <p className="mb-6 text-sm text-[#2C2731]/80">
            Your package has been successfully purchased. You'll receive a confirmation email shortly.
          </p>

          {orderId && (
            <p className="mb-6 text-sm text-[#2C2731]/70">
              Order ID: <span className="font-mono">{orderId}</span>
            </p>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/home')}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#304031] px-6 py-3 text-base font-semibold text-[#FEF6D6] transition-colors duration-200 hover:bg-[#1F1924] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]"
            >
              Return to Home
            </button>
            <button
              onClick={() => navigate('/home/chat')}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#FFFCEF] px-6 py-3 text-base font-semibold text-[#304031] transition-colors duration-200 hover:bg-[#304031]/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]"
            >
              Message Your Guide
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutSuccessPage;
